import Styles from './setup.module.scss'
import React, { useState } from 'react';
import {
    Typography,
    TextField,
    IconButton,
    InputAdornment,
    LinearProgress,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import axios from 'axios';

export const PasswordSetupForm = ({ onSuccess, initialData = {}, context, authContext }) => {
    const [formData, setFormData] = useState({
        password: initialData.password || '',
        confirmPassword: initialData.confirmPassword || '',
    });
    const [touched, setTouched] = useState({});
    const [errors, setErrors] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const getPasswordStrength = (password) => {
        if (!password) return 0;

        let strength = 0;

        if (password.length > 0) strength += 10
        // Length check
        if (password.length >= 8) strength += 15;

        // Contains uppercase
        if (/[A-Z]/.test(password) && password.length >= 8) strength += 25;

        // Contains number
        if (/\d/.test(password) && password.length >= 8) strength += 25;

        // Contains special character
        if (/[^A-Za-z0-9]/.test(password) && password.length >= 8) strength += 25;

        return strength;
    };

    const getStrengthLabel = (strength) => {
        if (strength === 0) return 'No Password';
        if (strength <= 15) return 'Too Weak';
        if (strength <= 25) return 'Weak';
        if (strength <= 50) return 'Fair';
        if (strength <= 75) return 'Good';
        return 'Strong';
    };

    const getStrengthColor = (strength) => {
        if (strength <= 25) return '#f44336'; // red
        if (strength <= 50) return '#ff9800'; // orange
        if (strength <= 75) return '#2196f3'; // blue
        return '#4caf50'; // green
    };

    const passwordStrength = getPasswordStrength(formData.password);
    const strengthLabel = getStrengthLabel(passwordStrength);
    const strengthColor = getStrengthColor(passwordStrength);

    const validate = () => {
        const newErrors = {};

        if (!formData.password || formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters long';
        } else if (!/[A-Z]/.test(formData.password)) {
            newErrors.password = 'Password must contain at least one uppercase letter';
        } else if (!/[a-z]/.test(formData.password)) {
            newErrors.password = 'Password must contain at least one lowercase letter';
        } else if (!/\d/.test(formData.password)) {
            newErrors.password = 'Password must contain at least one number';
        } else if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password)) {
            newErrors.password = 'Password must contain at least one special character';
        }

        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }


        return newErrors;
    };

    const handleBlur = (field) => {
        setTouched((prev) => ({ ...prev, [field]: true }));
        setErrors(validate());
    };

    const handleChange = (field) => (e) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        const formErrors = validate();
        setErrors(formErrors);
        setLoading(true);
        setTouched({ password: true, confirmPassword: true, });

        if (Object.keys(formErrors).length !== 0) return;

        try {
            const response = await axios.patch('/api/user', { password: formData.password });
            if (response.status === 200) {
                authContext.update({ has_password: true })
                context.notification.show(response?.data?.message || 'OTP Sent', 'success', true);
                onSuccess(formData);
            }
        } catch (error) {
            if (error.response?.data?.message) {
                context.notification.show(error.response.data?.message, 'error');
            } else {
                context.notification.show(error.response.data, 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1em', color: '#1450a0' }}>Set Your Password</Typography>
            <div className={Styles.formContainer}>
                <TextField
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange('password')}
                    onBlur={() => handleBlur('password')}
                    disabled={loading}
                    error={touched.password && !!errors.password}
                    helperText={touched.password && errors.password ? errors.password : 'Must be at least 8 characters, include uppercase, number, and special character'}
                    required
                    fullWidth
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                            style: {
                                height: "50px",
                            }
                        }
                    }}
                />
                {formData.password && (
                    <div id='passwordStrengthContainer' className={Styles.passwordStrengthContainer}>
                        <div id='passwordStrength' className={Styles.passwordStrength}>
                            <Typography variant="caption">Password Strength: {strengthLabel}</Typography>
                            <Typography variant="caption" style={{ color: strengthColor }}>{passwordStrength}%</Typography>
                        </div>
                        <LinearProgress
                            variant="determinate"
                            value={passwordStrength}
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: 'rgba(0,0,0,0.08)',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: strengthColor,
                                }
                            }}
                        />
                    </div>
                )}
                <TextField
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword || ''}
                    onChange={handleChange('confirmPassword')}
                    onBlur={() => handleBlur('confirmPassword')}
                    error={touched.confirmPassword && !!errors.confirmPassword}
                    helperText={touched.confirmPassword && errors.confirmPassword}
                    disabled={loading}
                    required
                    fullWidth
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle confirm password visibility"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        edge="end"
                                    >
                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                            style: {
                                height: "50px",
                            }
                        }
                    }}
                />
            </div>
            <div id='setPasswordButton' className={Styles.buttonContainer}>
                <AivagamButton
                    buttonText={loading ? 'Setting...' : 'Set Password'}
                    type="submit"
                    disabled={loading}
                    onClick={handleSubmit}
                    classStyles={{
                        height: '3em',
                        padding: "0.5em 2em",
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                        fontSize: '1em'
                    }}
                />
            </div>
        </>
    );
};
